import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import api from './api';
import { Company, CompanyCreate, CompanyUpdate, CompanyFilters } from '../../types/company';
import { CompanyStats, BillingAccount, BillingAccountUpdate, BillingStatus } from '../../types/billing';

export class CompanyService extends BaseService {
  /**
   * Get all companies with optional filtering
   */
  static async getAll(filters?: CompanyFilters): Promise<Company[]> {
    try {
      let endpoint = API_ENDPOINTS.COMPANIES.BASE;
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.industry) params.append('industry', filters.industry);
        if (filters.size) params.append('size', filters.size);
        if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active));
        if (filters.limit) params.append('limit', String(filters.limit));
        if (filters.offset) params.append('offset', String(filters.offset));
      }
      
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
      
      return this.get<Company[]>(endpoint);
    } catch (error) {
      console.error('[CompanyService] Error fetching companies:', error);
      return [];
    }
  }

  /**
   * Get company by ID
   */
  static async getById(id: string | null | undefined): Promise<Company | null> {
    // Validate that ID is provided and not null/undefined
    if (!id || id === 'null' || id === 'undefined') {
      console.warn('[CompanyService] getById called with invalid ID:', id);
      return null;
    }
    
    try {
      return await this.get<Company>(API_ENDPOINTS.COMPANIES.BY_ID(id));
    } catch (error) {
      console.error('[CompanyService] Error fetching company:', error);
      return null;
    }
  }

  /**
   * Create a new company
   */
  static async create(data: CompanyCreate): Promise<Company> {
    return this.post<Company>(
      API_ENDPOINTS.COMPANIES.BASE,
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Update company
   */
  static async update(id: string, data: CompanyUpdate): Promise<Company> {
    return this.put<Company>(
      API_ENDPOINTS.COMPANIES.BY_ID(id),
      data as unknown as Record<string, unknown>
    );
  }

  /**
   * Delete company
   */
  static async delete(id: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.COMPANIES.BY_ID(id));
    } catch (error: any) {
      // Suppress CORS/network errors
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error(`DELETE request failed for ${API_ENDPOINTS.COMPANIES.BY_ID(id)}:`, error);
      }
      throw error;
    }
  }

  /**
   * Get users in a company
   */
  static async getUsers(companyId: string): Promise<any[]> {
    try {
      // This endpoint might need to be implemented on backend
      // For now, we'll use the company-instances endpoint to get users
      const instances = await this.get<any[]>(
        `${API_ENDPOINTS.COMPANY_INSTANCES.BASE}?company_id=${companyId}`
      );
      return instances;
    } catch (error) {
      console.error('[CompanyService] Error fetching company users:', error);
      return [];
    }
  }

  /**
   * Get company analytics
   */
  static async getAnalytics(companyId: string): Promise<any> {
    // This endpoint might need to be implemented on backend
    // Placeholder for now
    return {};
  }

  /**
   * Get company statistics (user count, subscription count, billing status, and artifact/workflow stats)
   */
  static async getCompanyStats(companyId: string): Promise<CompanyStats | null> {
    try {
      const response = await this.get<any>(API_ENDPOINTS.COMPANIES.STATS(companyId));
      
      // Map backend response to CompanyStats type
      // Backend returns: instance_count, artifact_count, workflow_count, artifact_item_count, last_artifact_created
      // Frontend expects: company_id, user_count, subscription_count, billing_status, plus optional artifact/workflow fields
      const stats: CompanyStats = {
        company_id: companyId,
        // Artifact/workflow stats from backend
        instance_count: response.instance_count ?? 0,
        artifact_count: response.artifact_count ?? 0,
        workflow_count: response.workflow_count ?? 0,
        artifact_item_count: response.artifact_item_count ?? 0,
        last_artifact_created: response.last_artifact_created ?? null,
        // User/subscription/billing stats - not provided by backend, will use fallback
        user_count: response.user_count ?? 0,
        subscription_count: response.subscription_count ?? 0,
        billing_status: (response.billing_status as BillingStatus) ?? 'No Billing Account',
      };
      
      return stats;
    } catch (error) {
      console.error('[CompanyService] Error fetching company stats:', error);
      // Return default stats if endpoint doesn't exist yet
      return {
        company_id: companyId,
        user_count: 0,
        subscription_count: 0,
        billing_status: 'No Billing Account',
      };
    }
  }

  /**
   * Get billing account for a company
   */
  static async getBillingAccount(companyId: string): Promise<BillingAccount | null> {
    try {
      return await this.get<BillingAccount>(API_ENDPOINTS.COMPANIES.BILLING(companyId));
    } catch (error: any) {
      // If 404, return null (no billing account exists)
      if (error.response?.status === 404) {
        return null;
      }
      console.error('[CompanyService] Error fetching billing account:', error);
      return null;
    }
  }

  /**
   * Update billing account for a company
   */
  static async updateBillingAccount(
    companyId: string,
    data: BillingAccountUpdate
  ): Promise<BillingAccount> {
    return this.put<BillingAccount>(
      API_ENDPOINTS.COMPANIES.BILLING(companyId),
      data as unknown as Record<string, unknown>
    );
  }
}
